import { useState } from 'react';
import Papa from 'papaparse';
import { Create_User } from '../../services/oprations/Users';

export const useCSVImport = (managers, dispatch, toast, refreshUsers) => {
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const managersList = typeof managers === 'string' ? JSON.parse(managers) : (managers || []);

  const handleImportClick = () => {
    setIsImportModalOpen(true);
    setCsvFile(null);
    setCsvPreview([]);
    setImportErrors([]);
    setImportProgress({ current: 0, total: 0 });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      toast.error('Please upload a valid CSV file');
      return;
    }
    
    setCsvFile(file);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvPreview(results.data.slice(0, 5));
        
        const requiredColumns = ['user_name', 'user_email', 'user_phone', 'user_department', 
                                  'user_roll', 'manager_Id', 'shift_start', 'shift_end', 
                                  'timezone', 'viewtimezone'];
        const missingColumns = requiredColumns.filter(col => !results.meta.fields.includes(col));
        
        if (missingColumns.length > 0) {
          setImportErrors([`Missing required columns: ${missingColumns.join(', ')}`]);
          return;
        }
        
        const errors = [];
        results.data.forEach((row, index) => {
          if (!row.user_name || !row.user_email || !row.user_phone || 
              !row.user_department || !row.user_roll || !row.manager_Id) {
            errors.push(`Row ${index + 2}: Missing required fields`);
          }
          if (row.user_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.user_email)) {
            errors.push(`Row ${index + 2}: Invalid email format`);
          }
        });
        setImportErrors(errors);
      },
      error: () => {
        toast.error('Failed to parse CSV file');
        setImportErrors(['Failed to parse CSV file.']);
      }
    });
  };

  const handleImportSubmit = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    if (importErrors.length > 0) {
      toast.error('Please fix CSV errors before importing');
      return;
    }
    
    setIsImporting(true);
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const usersToImport = results.data;
        let successCount = 0, errorCount = 0;
        
        for (let i = 0; i < usersToImport.length; i++) {
          const u = usersToImport[i];
          setImportProgress({ current: i + 1, total: usersToImport.length });
          
          let managerId = u.manager_Id;
          if (managerId && !managerId.match(/^[0-9a-fA-F]{24}$/)) {
            const manager = managersList.find(m => m.manager_email === managerId);
            if (manager) managerId = manager._id;
          }
          
          try {
            const res = await dispatch(Create_User({ 
              ...u, 
              manager_Id: managerId, 
              shift_start: u.shift_start || '09:00:00', 
              shift_end: u.shift_end || '18:00:00', 
              timezone: u.timezone || 'Asia/Calcutta', 
              viewtimezone: u.viewtimezone || 'Asia/Calcutta' 
            }));
            
            if (res?.data?.success) successCount++;
            else errorCount++;
          } catch {
            errorCount++;
          }
          
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        if (successCount > 0) {
          toast.success(`Imported ${successCount} users${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
        }
        if (errorCount > 0) toast.error(`${errorCount} users failed to import.`);
        
        setIsImportModalOpen(false);
        setCsvFile(null);
        setCsvPreview([]);
        setImportErrors([]);
        setImportProgress({ current: 0, total: 0 });
        setIsImporting(false);
        await refreshUsers();
      },
      error: () => {
        toast.error('Failed to parse CSV file');
        setIsImporting(false);
      }
    });
  };

  return {
    isImportModalOpen,
    setIsImportModalOpen,
    csvFile,
    isImporting,
    csvPreview,
    importErrors,
    importProgress,
    handleFileUpload,
    handleImportSubmit,
    handleImportClick
  };
};